// ==================================================================================
// Digital Approval System (DAS) - Reports Service
// ==================================================================================
// Axios calls for the Reports module — the Workflow Summary dashboard (U6)
// under My Reports.
// ==================================================================================

import { axiosClient } from '../../client/axiosClient';
import { ApiResponse, PagedResult } from '../../types/common.types';
import {
  UserWorkflowSummaryDto,
  RequestReportQuery,
  RequestReportRow,
  PendingDaysBreakdown,
} from '../../types/das/reports.types';
import { reportsEndpoints } from '../../endpoints/das/reports.endpoints';

/**
 * Reports service
 */
export const reportsService = {
  /**
   * Workflow Summary dashboard (U6) — all-time snapshot, no query params.
   */
  getUserWorkflowSummary: async (): Promise<UserWorkflowSummaryDto> => {
    const response = await axiosClient.get<ApiResponse<UserWorkflowSummaryDto>>(
      reportsEndpoints.getUserWorkflowSummary()
    );
    return response.data.data;
  },

  /**
   * One page of a request-lifecycle report. Server enforces role-based data
   * scope (all requests vs the caller's own flow).
   */
  getRequestsReport: async (
    query: RequestReportQuery
  ): Promise<PagedResult<RequestReportRow>> => {
    const response = await axiosClient.get<ApiResponse<PagedResult<RequestReportRow>>>(
      reportsEndpoints.getRequestsReport(query)
    );
    return response.data.data;
  },

  /**
   * Per-approver pending-days breakdown for one request (drill-down popup).
   */
  getPendingBreakdown: async (requestId: string): Promise<PendingDaysBreakdown> => {
    const response = await axiosClient.get<ApiResponse<PendingDaysBreakdown>>(
      reportsEndpoints.getPendingBreakdown(requestId)
    );
    return response.data.data;
  },

  /**
   * Downloads the Excel export for a report and triggers a browser save.
   * Uses a blob response so the (permission-scoped, full) file streams from the
   * backend — the grid never holds the whole dataset.
   */
  downloadRequestsReport: async (
    query: RequestReportQuery,
    appliedFilters?: string[]
  ): Promise<void> => {
    let endpointUrl = reportsEndpoints.exportRequestsReport(query);
    if (appliedFilters && appliedFilters.length > 0) {
      // Repeated `appliedFilters` params → List<string> on the backend (used only
      // to stamp the criteria into the Excel banner, not to filter data).
      const extra = appliedFilters
        .map((f) => `appliedFilters=${encodeURIComponent(f)}`)
        .join('&');
      endpointUrl += (endpointUrl.includes('?') ? '&' : '?') + extra;
    }

    const response = await axiosClient.get(endpointUrl, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    // Prefer the server-provided filename from Content-Disposition when present.
    const disposition = (response.headers?.['content-disposition'] as string) || '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const fileName = match
      ? decodeURIComponent(match[1])
      : `${query.reportType}-Requests-Report.xlsx`;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};
